/**
 * Custom hook for managing coding tasks
 * Handles code execution, submission, and task state management
 */

import { useState, useCallback } from 'react';
import axios from 'axios';
import { CodingTask, CodeExecutionResult, CodeSubmission } from '../types';
import { API_BASE, getAuthHeaders } from '../../utils/api';

interface UseCodingTasksReturn {
  tasks: CodingTask[];
  selectedTask: CodingTask | null;
  submissions: Record<string, CodeSubmission[]>;
  loading: boolean;
  error: string | null;
  isExecuting: boolean;
  isPushing: boolean;
  lastExecutionResult: CodeExecutionResult | null;
  
  loadTasks: (sessionId: string) => Promise<void>;
  selectTask: (taskId: string) => void;
  executeCode: (taskId: string, code: string, language: string) => Promise<CodeExecutionResult>;
  pushCode: (taskId: string, code: string, language: string) => Promise<{ success: boolean; message: string }>;
  getTaskHistory: (taskId: string) => Promise<CodeSubmission[]>;
}

export function useCodingTasks(sessionId: string | null): UseCodingTasksReturn {
  const [tasks, setTasks] = useState<CodingTask[]>([]);
  const [selectedTask, setSelectedTask] = useState<CodingTask | null>(null);
  const [submissions, setSubmissions] = useState<Record<string, CodeSubmission[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const [isPushing, setIsPushing] = useState(false);
  const [lastExecutionResult, setLastExecutionResult] = useState<CodeExecutionResult | null>(null);

  const loadTasks = useCallback(async (sessionId: string) => {
    if (!sessionId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const authHeaders = await getAuthHeaders();
      const response = await axios.get(`${API_BASE}/chat/tasks`, {
        params: { session_id: sessionId },
        headers: authHeaders
      });
      
      if (response.data.status === 'success') {
        const allObjectives = response.data.data.objectives || [];
        
        // Filter for coding tasks only
        const codingTasks: CodingTask[] = allObjectives
          .filter((obj: any) => obj.task_type === 'coding')
          .map((obj: any) => ({
            task_id: obj.objective_id,
            title: obj.title,
            description: obj.description,
            task_type: obj.task_type,
            language: obj.coding_task_data?.language,
            starter_code: obj.coding_task_data?.starter_code,
            constraints: obj.coding_task_data?.constraints || [],
            test_cases: obj.coding_task_data?.test_cases || [],
            time_budget_minutes: obj.coding_task_data?.time_budget_minutes,
            system_context: obj.coding_task_data?.system_context,
            hints: obj.coding_task_data?.hints || []
          }));
        
        setTasks(codingTasks);
        
        // Auto-select first task if none selected
        if (codingTasks.length > 0 && !selectedTask) {
          setSelectedTask(codingTasks[0]);
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load tasks';
      setError(errorMessage);
      console.error('Failed to load coding tasks:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedTask]);

  const selectTask = useCallback((taskId: string) => {
    const task = tasks.find(t => t.task_id === taskId);
    if (task) {
      setSelectedTask(task);
      setLastExecutionResult(null);
    }
  }, [tasks]);

  const executeCode = useCallback(async (
    taskId: string,
    code: string,
    language: string
  ): Promise<CodeExecutionResult> => {
    if (!sessionId) {
      throw new Error('Session ID is required');
    }
    
    setIsExecuting(true);
    setError(null);
    
    try {
      const authHeaders = await getAuthHeaders();
      const response = await axios.post(
        `${API_BASE}/sessions/${sessionId}/tasks/execute`,
        {
          task_id: taskId,
          language,
          code,
          stdin: '',
          timeout_ms: 3000
        },
        { headers: authHeaders }
      );
      
      if (response.data.status === 'success') {
        const result: CodeExecutionResult = response.data.data;
        setLastExecutionResult(result);
        return result;
      } else {
        throw new Error(response.data.message || 'Execution failed');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to execute code';
      setError(errorMessage);
      console.error('Code execution error:', err);
      throw err;
    } finally {
      setIsExecuting(false);
    }
  }, [sessionId]);

  const pushCode = useCallback(async (
    taskId: string,
    code: string,
    language: string
  ): Promise<{ success: boolean; message: string }> => {
    if (!sessionId) {
      throw new Error('Session ID is required');
    }
    
    setIsPushing(true);
    setError(null);
    
    try {
      const authHeaders = await getAuthHeaders();
      const response = await axios.post(
        `${API_BASE}/sessions/${sessionId}/tasks/push`,
        {
          task_id: taskId,
          language,
          code,
          stdin: '',
          timeout_ms: 3000
        },
        { headers: authHeaders }
      );
      
      if (response.data.status === 'success') {
        // Record submission
        const submission: CodeSubmission = {
          task_id: taskId,
          code,
          language,
          timestamp: new Date().toISOString(),
        };
        
        setSubmissions(prev => ({
          ...prev,
          [taskId]: [...(prev[taskId] || []), submission]
        }));
        
        return {
          success: true,
          message: response.data.data.message || 'Pushed successfully'
        };
      } else {
        throw new Error(response.data.message || 'Push failed');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to push code';
      setError(errorMessage);
      console.error('Code push error:', err);
      return {
        success: false,
        message: errorMessage
      };
    } finally {
      setIsPushing(false);
    }
  }, [sessionId]);

  const getTaskHistory = useCallback(async (taskId: string): Promise<CodeSubmission[]> => {
    // Return cached submissions for now
    // In a full implementation, this would fetch from backend
    return submissions[taskId] || [];
  }, [submissions]);

  return {
    tasks,
    selectedTask,
    submissions,
    loading,
    error,
    isExecuting,
    isPushing,
    lastExecutionResult,
    loadTasks,
    selectTask,
    executeCode,
    pushCode,
    getTaskHistory
  };
}

